import { API_URL } from '../constants/config';
import { apiFetchWithTokenRefresh } from '../utils/tokenInterceptor';

export interface InviteResponse {
  id: number;
  code: string;
  inviter_id: number;
  inviter_name?: string;
  inviter_email?: string;
  invitee_id?: number | null;
  invitee_name?: string | null;
  invitee_email?: string | null;
  created_at: string;
  used_at?: string | null;
}

export interface InviteListResponse {
  items: InviteResponse[];
  total: number;
}

const apiFetch = async (url: string, options: RequestInit = {}) => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Use token refresh wrapper which handles token refresh automatically
  const response = await apiFetchWithTokenRefresh(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP error! status: ${response.status}`);
  }

  return response;
};

export const invitesAPI = {
  /**
   * Get invite information by code. Requires authentication.
   */
  getByCode: async (code: string): Promise<InviteResponse> => {
    const response = await apiFetch(`${API_URL}/invites/${code}`);
    return response.json();
  },

  /**
   * Create a new invite code
   */
  create: async (code: string): Promise<InviteResponse> => {
    const response = await apiFetch(`${API_URL}/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    return response.json();
  },

  /**
   * List invites (user's own invites, or all for admins)
   */
  list: async (
    skip: number = 0,
    limit: number = 50
  ): Promise<InviteListResponse> => {
    const response = await apiFetch(
      `${API_URL}/invites?skip=${skip}&limit=${limit}`
    );
    return response.json();
  },

  /**
   * Admin: List all invites with filtering
   */
  adminList: async (
    skip: number = 0,
    limit: number = 100,
    inviterId?: number,
    inviteeId?: number,
    used?: boolean
  ): Promise<InviteListResponse> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    if (inviterId) params.append('inviter_id', inviterId.toString());
    if (inviteeId) params.append('invitee_id', inviteeId.toString());
    if (used !== undefined) params.append('used', used.toString());

    const response = await apiFetch(
      `${API_URL}/admin/invites?${params.toString()}`
    );
    return response.json();
  },
};
